import {
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    styled,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import {
    ApplicationFilterItemsStore,
    IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import ApplicationFilterItem from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/ApplicationFilterItem";
import { AddOutlined, Close } from "@mui/icons-material";
import LLMClassifierService from "services/LLMClassifierService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import RootStore from "stores/RootStore";
import { LLMCLassifierDetailsResponse } from "models/LLMs";

type RunOnHistoryButtonProps = {
    classifiers: LLMCLassifierDetailsResponse[];
    selectedClassifier?: LLMCLassifierDetailsResponse;
} & Omit<React.ComponentPropsWithoutRef<typeof AcxButton>, "onClick">;

const FiltersBarContainer = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
}));

const FilterItemPaper = styled(Paper)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    boxShadow: "0px 1px 2px 0px #00000012",
}));

const MAX_CONVERSATIONS = 1000;

export const RunOnHistoryButton = observer(function ({
    classifiers,
    selectedClassifier,
    disabled,
    ...buttonProps
}: RunOnHistoryButtonProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSelectFilterVisible, setIsSelectFilterVisible] = useState(false);
    const [dialogClassifierId, setDialogClassifierId] = useState<
        string | undefined
    >();
    const [selectKey, setSelectKey] = useState(0);

    const messageStore = useStore(MessageStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);

    const llmClassifierService = useMemo(() => new LLMClassifierService(), []);

    // Reset selected classifier when dialog opens
    useEffect(() => {
        if (isOpen) {
            setDialogClassifierId(selectedClassifier?.id);
            setSelectKey((prev) => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const classifierOptions = useMemo(
        () => classifiers.map((c) => ({ label: c.name, value: c.id })),
        [classifiers],
    );

    const defaultClassifierOption = useMemo(
        () => classifierOptions.find((o) => o.value === selectedClassifier?.id),
        [classifierOptions, selectedClassifier?.id],
    );

    // Create a new instance store when dialog opens
    const instanceStore = useMemo(() => {
        if (!isOpen) return null;
        const store = new ApplicationFiltersInstanceStore(
            RootStore(),
            "Run On History Filters",
        );
        return store;
    }, [isOpen]);

    const appliedFilterCount = instanceStore?.appliedFilterCount;
    const filterLabel = useMemo(() => {
        if (!appliedFilterCount) return "0 Filters Applied";
        if (appliedFilterCount === 1) return "1 Filter Applied";
        return `${appliedFilterCount} Filters Applied`;
    }, [appliedFilterCount]);

    const handleClearFilters = () => {
        applicationFilterItemsStore.setSelectedFilterItems([]);
        instanceStore?.resetFilters();
    };

    const handleClose = () => {
        setIsOpen(false);
        setIsSelectFilterVisible(false);
        applicationFilterItemsStore.setSelectedFilterItems([]);
    };

    const handleConfirmAndRun = async () => {
        if (!dialogClassifierId || !instanceStore) return;

        setIsLoading(true);
        try {
            const filters = instanceStore.toRequestObject();
            const response = await llmClassifierService.targetedReclassify(
                dialogClassifierId,
                {
                    limit: MAX_CONVERSATIONS,
                    filters,
                },
            );

            messageStore.logInfo(
                `Successfully queued ${response.numberOfConversationsReclassified} conversations for classification.`,
            );
            handleClose();
        } catch (error) {
            messageStore.logError(
                "Failed to run model on history. Please try again.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const hasDateRange = instanceStore?.startDate && instanceStore?.endDate;

    return (
        <React.Fragment>
            <AcxButton
                color="white"
                {...buttonProps}
                disabled={disabled}
                onClick={() => setIsOpen(true)}
            >
                Run on History
            </AcxButton>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { minHeight: "500px" },
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        flexWrap="nowrap"
                    >
                        {/* Header */}
                        <Grid
                            container
                            item
                            justifyContent="space-between"
                            alignItems="flex-start"
                            px={2}
                            pt={2}
                            pb={1}
                        >
                            <Grid item>
                                <Typography
                                    sx={(theme) => ({
                                        fontWeight: 600,
                                        fontSize: "20px",
                                        color: theme.palette.gray[700],
                                        lineHeight: "32px",
                                    })}
                                >
                                    Run on past conversations
                                </Typography>
                                <Typography
                                    sx={(theme) => ({
                                        color: theme.palette.gray[500],
                                        fontSize: "14px",
                                    })}
                                >
                                    Apply this model to a targeted set of past
                                    conversations for validation and insights.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" onClick={handleClose}>
                                    <Close />
                                </IconButton>
                            </Grid>
                        </Grid>

                        {/* Model Selected */}
                        <Grid
                            container
                            item
                            direction="column"
                            rowGap={1}
                            px={2}
                            py={1}
                        >
                            <Typography
                                sx={(theme) => ({
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: theme.palette.gray[700],
                                })}
                            >
                                Model Selected
                            </Typography>
                            <AcxSelectSingle
                                key={selectKey}
                                id="run-on-history-model-select"
                                valueField="value"
                                labelField="label"
                                fullWidth
                                options={classifierOptions}
                                defaultValue={defaultClassifierOption}
                                onChange={(option: {
                                    label: string;
                                    value: string;
                                }) => {
                                    setDialogClassifierId(option.value);
                                }}
                                placeholder="Select a model..."
                            />
                        </Grid>

                        {/* Select Filters */}
                        <Grid
                            container
                            item
                            direction="column"
                            rowGap={1}
                            px={2}
                            py={1}
                            sx={(theme) => ({
                                backgroundColor: "#F9FAFB",
                                borderTop: `1px solid ${theme.palette.gray[200]}`,
                            })}
                        >
                            <Grid item>
                                <Typography
                                    sx={(theme) => ({
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        color: theme.palette.gray[700],
                                    })}
                                >
                                    Select Filters
                                </Typography>
                                <Typography
                                    sx={(theme) => ({
                                        color: theme.palette.gray[500],
                                        fontSize: "14px",
                                    })}
                                >
                                    You may run this on up to{" "}
                                    {MAX_CONVERSATIONS.toLocaleString()}{" "}
                                    conversations within the following filters
                                </Typography>
                            </Grid>

                            {/* Filters Applied Bar */}
                            <FiltersBarContainer
                                container
                                item
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="nowrap"
                            >
                                <Grid item>
                                    <Typography>{filterLabel}</Typography>
                                </Grid>
                                <Grid item>
                                    <AcxButton
                                        color="primary"
                                        variant="text"
                                        onClick={handleClearFilters}
                                        sx={(theme) => ({
                                            color: theme.palette.primary.main,
                                            ":hover": {
                                                backgroundColor:
                                                    theme.palette.white.main,
                                            },
                                        })}
                                    >
                                        Clear
                                    </AcxButton>
                                </Grid>
                            </FiltersBarContainer>

                            {/* Filter Items */}
                            {instanceStore && (
                                <Stack
                                    direction="column"
                                    rowGap={2}
                                    sx={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                    }}
                                >
                                    {/* Date Range Filter - Always Present */}
                                    <FilterItemPaper>
                                        <ApplicationFilterItem
                                            title="Date Range"
                                            filterKey="dateRange"
                                            store={instanceStore}
                                            instanceStore={instanceStore}
                                            isClearable={false}
                                        />
                                    </FilterItemPaper>

                                    {applicationFilterItemsStore.selectedFilterItems.map(
                                        (item) => (
                                            <FilterItemPaper
                                                key={item.filterKey}
                                            >
                                                <ApplicationFilterItem
                                                    {...item}
                                                    store={instanceStore}
                                                    instanceStore={
                                                        instanceStore
                                                    }
                                                />
                                            </FilterItemPaper>
                                        ),
                                    )}

                                    {applicationFilterItemsStore
                                        .selectedFilterItems.length > 0 && (
                                        <Divider />
                                    )}

                                    {/* Add Filter Dropdown */}
                                    {isSelectFilterVisible && (
                                        <FilterItemPaper>
                                            <AcxSelectSingle
                                                id="run-on-history-add-filter-select"
                                                options={applicationFilterItemsStore.availableFilterGroups
                                                    .map((group) => ({
                                                        label: group.title,
                                                        options:
                                                            group.items.filter(
                                                                (item) =>
                                                                    item.filterKey !==
                                                                    "dateRange",
                                                            ),
                                                    }))
                                                    .filter(
                                                        (group) =>
                                                            group.options
                                                                .length > 0,
                                                    )}
                                                labelField="title"
                                                valueField=""
                                                noValue
                                                onChange={(
                                                    option: IApplicationFilterItem,
                                                ) => {
                                                    applicationFilterItemsStore.addSelectedFilterItem(
                                                        option,
                                                    );
                                                    setIsSelectFilterVisible(
                                                        false,
                                                    );
                                                }}
                                                placeholder="Filter by..."
                                            />
                                        </FilterItemPaper>
                                    )}

                                    {/* Add Filter Button */}
                                    <AcxButton
                                        color="primary"
                                        onClick={() =>
                                            setIsSelectFilterVisible(true)
                                        }
                                        sx={(theme) => ({
                                            color: theme.palette.blue.main,
                                            backgroundColor:
                                                theme.palette.white.main,
                                            paddingInline: theme.spacing(2),
                                            paddingBlock: theme.spacing(1),
                                            borderRadius:
                                                theme.shape.borderRadius,
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            lineHeight: "20px",
                                            ":hover": {
                                                backgroundColor:
                                                    theme.palette.white.main,
                                                opacity: "80%",
                                            },
                                        })}
                                        startIcon={
                                            <AddOutlined
                                                sx={{ fontSize: "20px" }}
                                            />
                                        }
                                    >
                                        Filter
                                    </AcxButton>
                                </Stack>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions
                    sx={(theme) => ({
                        borderTop: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(2, 3),
                    })}
                >
                    <AcxButton color="white" onClick={handleClose}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        onClick={handleConfirmAndRun}
                        loading={isLoading}
                        disabled={!hasDateRange || !dialogClassifierId}
                        disabledTooltip={
                            !dialogClassifierId
                                ? "Please select a model"
                                : "Please select a date range"
                        }
                    >
                        Confirm and Run
                    </AcxButton>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
});
